import produce from "immer";
import { MutableRefObject, Suspense, useContext, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  AvailableIntegrationFragment,
  Connection,
  IntegrationAbility,
  IntegrationConfigurationFragment,
  SubscriptionStatus,
  useEltConnectionSettingsLazyQuery,
  useIntegrationConfigurationSuspenseQuery,
} from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import { ActionButton } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { SectionHeading } from "@/components/elements/Typography";
import Center from "@/components/elements/layout/Center";
import { ConnectorsLimitAlert } from "@/components/modules/PlanAlerts";
import { ErrorBoundary } from "@/components/primitives/ErrorBoundary";
import {
  ConnectorOption as ConnectorOptionType,
  useConnectorOptions,
} from "@/features/connectors";
import {
  CustomConnectorAccessGuard,
  CustomConnectorTeaser,
  useCustomConnectorAccess,
} from "@/features/feature-guards";
import {
  useConnectorAdditionValidationUpgradeDialog,
  useSubscriptionStatus,
  useWeldPlanLimits,
} from "@/features/subscription";
import { useMountEffect } from "@/hooks/useMountEffect";
import {
  isCustomConnector,
  useCreateConnection,
  useIntegrationsMap,
} from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import ConnectionForm, {
  AuthorizeMethod,
  ConnectionFormSubmitButton,
  useConnectionForm,
} from "@/pages/Connections/ConnectionForm/ConnectionForm";
import {
  ConnectionSlideOverContext,
  ConnectionSlideOverContextProvider,
} from "@/pages/Connections/NewConnectionSlideOver";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useToast } from "@/providers/ToastProvider";
import {
  ConnectorOption,
  Connectors,
  SearchFilter,
} from "@/widgets/connectors";
import { RequestConnector } from "@/widgets/request-connector";

import { useStepsContext } from "../../../stepper/useSteps";
import { Content, Footer } from "../../components/Layout";
import { NavButton } from "../../components/NavButton";
import { useDataSourceStepContext } from "../../contexts/DataSourceStepsContext";
import {
  createInitialState,
  useDataSourceStateContext,
} from "../../step-state";
import IPWhitelistInfo from "./IPWhitelistInfo";

export function SourceStepContainer() {
  const { state, setState } = useDataSourceStateContext();
  const { onGoToStep } = useStepsContext();
  const integrations = useIntegrationsMap();
  const toast = useToast();
  const mixpanel = useMixpanel();

  const [fetchConnectionSettings, { loading }] =
    useEltConnectionSettingsLazyQuery();
  return (
    <>
      <ConnectionSlideOverContextProvider
        abilityFilter={IntegrationAbility.EltSource}
        integrationId={state.source.value.integrationId}
      >
        <ConnectionViews
          integrationId={state.source.value.integrationId}
          onConnectionAdded={async (connection) => {
            setState(
              produce(state, (draft) => {
                const sourceValues = {
                  value: {
                    integrationId: connection.integrationId,
                    sourceId: connection.id,
                  },
                  isValid: true,
                  isDirty: false,
                };

                const currentSourceId = state.source.value.sourceId;
                if (currentSourceId && currentSourceId !== connection.id) {
                  // Changing source means we need to reset state in all other steps
                  const newState = createInitialState();
                  newState.source = sourceValues;
                  return newState;
                } else {
                  draft.source = sourceValues;
                }
              }),
            );

            const { data, error } = await fetchConnectionSettings({
              variables: {
                input: {
                  id: connection.id,
                },
              },
            });

            if (data === undefined && error) {
              toast(
                `Connection failed for "${connection.label}"`,
                error.message,
                "error",
              );
              onGoToStep("source");
              // Reset the state to clear the connection
              setState(createInitialState());
              return;
            }

            // A connector has been selected, so now the sync setup will be begin.
            mixpanel.track("Data Source Setup Started");
            mixpanel.time_event("Data Source Setup Completed Step Viewed");

            // Figure out if we need to go to the connection settings step
            // or directly to the select tables step
            if (
              data?.getEltSettings.connectionSettings ||
              integrations.get(connection.integrationId)?.config
                ?.supportsStartDate
            ) {
              onGoToStep("source-settings");
            } else {
              onGoToStep("tables");
            }
          }}
        />
      </ConnectionSlideOverContextProvider>
      {loading && (
        <Center className="absolute inset-0 z-10 bg-white/70 dark:bg-black/30">
          <LoadingSpinner />
        </Center>
      )}
    </>
  );
}

function ConnectionViews(props: {
  integrationId?: string;
  onConnectionAdded: (
    connection: Pick<Connection, "id" | "label" | "integrationId">,
  ) => void;
}) {
  const { setState } = useDataSourceStateContext();
  const { onCancelSetup } = useDataSourceStepContext();
  const { integration, setIntegration } = useContext(
    ConnectionSlideOverContext,
  );
  const toast = useToast();
  const mixpanel = useMixpanel();

  const handleConnectionAdded = (
    connection: Pick<Connection, "id" | "label" | "integrationId">,
    usingExistingConnection: boolean = false,
  ) => {
    props.onConnectionAdded(connection);
    mixpanel.track("Connector Setup Succeeded", {
      integration_id: connection.integrationId,
      using_existing_connection: usingExistingConnection,
    });
  };

  const { loading: createConnectionLoading, createConnection } =
    useCreateConnection({
      onSuccess(connection) {
        handleConnectionAdded(connection);
      },
      onError(error, integrationId) {
        toast("Connector not created", error.message, "error");
        mixpanel.track("Connector Setup Failed", {
          integration_id: integrationId,
        });
      },
    });

  return (
    <div className="flex h-full flex-col">
      {integration === null ? (
        <SelectConnecterView
          onConnectionAdded={(connection) => {
            handleConnectionAdded(connection, true);
          }}
        />
      ) : (
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="flex justify-center p-8 pt-20">
                <div className="flex items-center gap-4">
                  <LoadingSpinner />
                  <span className="text-muted-foreground">
                    Loading connector configuration...
                  </span>
                </div>
              </div>
            }
          >
            <ConfigureConnectorView
              integration={integration}
              onSubmit={(integration, data) => {
                return createConnection(integration, data);
              }}
              isLoading={createConnectionLoading}
              onCancel={() => {
                setIntegration(null);
                setState(createInitialState());
              }}
              onClose={() => {
                onCancelSetup();
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
}

function SelectConnecterView(props: {
  onConnectionAdded: (
    connection: Pick<Connection, "id" | "label" | "integrationId">,
  ) => void;
}) {
  const { state, setState } = useDataSourceStateContext();
  const stepContext = useDataSourceStepContext();
  const { setIntegration } = useContext(ConnectionSlideOverContext);
  const mixpanel = useMixpanel();
  const trackDebounced = useDebouncedCallback(mixpanel.track, 500);

  const dwh = useDataWarehouseContext();
  const options = useConnectorOptions({ dwIntegration: dwh.integration });
  const { status } = useSubscriptionStatus();

  const { validateConnectionAddition, validationDialog } =
    useConnectorAdditionValidationUpgradeDialog();

  const handleIntegrationSelected = (option: ConnectorOptionType) => {
    validateConnectionAddition(option.integration.id, () => {
      setIntegration(option.integration);
      setState(
        produce(state, (draft) => {
          draft.source.value.integrationId = option.integration.id;
        }),
      );
    });
  };
  const { isConnectorsLimitReached } = useWeldPlanLimits();
  return (
    <>
      {isConnectorsLimitReached && (
        <div className="mb-4">
          <ConnectorsLimitAlert
            onClickConnections={() => stepContext.onCancelSetup()}
          />
        </div>
      )}

      <Connectors
        options={options}
        enableSelectExistingConnection
        disableCreateNewConnectionType={isConnectorsLimitReached}
        abilityFilter={IntegrationAbility.EltSource}
        // Return existing connection immediately
        onSelectConnection={props.onConnectionAdded}
        // Otherwise, open the connection configuration
        onSelectIntegration={handleIntegrationSelected}
        onOptionsFiltered={(filteredOptions, filters) => {
          if (filters.searchText.trim() === "") {
            return;
          }
          trackDebounced("Connection Options Filtered", {
            resultCount: filteredOptions.length,
            abilityFilter: filters.abilityFilter,
            searchText: filters.searchText.trim().toLowerCase(),
          });
        }}
      >
        {({ options }) => (
          <div className="flex h-full flex-1 flex-col gap-3 overflow-hidden">
            <div className="px-6 py-1">
              <SearchFilter
                ref={
                  stepContext.initialFocusRef as
                    MutableRefObject<HTMLInputElement | null> | undefined
                }
              />
            </div>
            <div className="flex flex-col gap-6 overflow-auto px-6 pt-1 pb-8">
              <div>
                <SectionHeading className="mb-2">All Connectors</SectionHeading>
                <div className="grid gap-4 sm:grid-cols-2">
                  {options.map((option) => (
                    <ConnectorOption
                      key={option.value}
                      option={option}
                      showAbilityLabels={false}
                      showPlanRequirement={
                        status === SubscriptionStatus.Freemium
                      }
                    />
                  ))}
                </div>
              </div>
              <div className="my-8 flex flex-col items-center">
                <p className="mb-2 block text-center text-muted-foreground">
                  Didn't find what you are looking for?
                </p>
                <RequestConnector />
              </div>
            </div>
            {validationDialog()}
          </div>
        )}
      </Connectors>
    </>
  );
}

function ConfigureConnectorView(props: {
  integration: AvailableIntegrationFragment;
  onSubmit: (
    integration: IntegrationConfigurationFragment,
    data: Record<string, any> & { label: string },
  ) => void;
  isLoading: boolean;
  onCancel: () => void;
  onClose: () => void;
}) {
  const { data } = useIntegrationConfigurationSuspenseQuery({
    variables: {
      id: props.integration.aliasMetadata?.id || props.integration.id,
    },
  });
  const integrationConfig = data.integrationConfiguration;

  const { isEnabled: customConnectorEnabled } = useCustomConnectorAccess(
    props.integration,
  );

  const formMethods = useConnectionForm(integrationConfig);
  const mixpanel = useMixpanel();

  useMountEffect(() => {
    mixpanel.time_event("Connector Setup Succeeded");
    mixpanel.track("Connector Setup Form Viewed", {
      integration_id: props.integration.id,
    });
  });

  const [externalAuthLinkCreated, setExternalAuthLinkCreated] = useState(false);
  const [authorizeMethod, setAuthorizeMethod] = useState<AuthorizeMethod>(
    AuthorizeMethod.Self,
  );

  return (
    <FormProvider {...formMethods}>
      <Content>
        <CustomConnectorAccessGuard
          integration={props.integration}
          fallback={() => (
            <div className="mx-auto w-full max-w-lg rounded-sm border bg-popover p-8">
              <CustomConnectorTeaser />
            </div>
          )}
        >
          {props.integration.aliasMetadata && (
            <Alert status="info" className="mb-4">
              <AlertIcon />
              <div>
                The {props.integration.name} connector is available via{" "}
                <strong>{props.integration.aliasMetadata.name}</strong>. Please
                view the documentation for more information.
              </div>
            </Alert>
          )}
          <form
            id="integration-form"
            onSubmit={formMethods.handleSubmit((values) =>
              props.onSubmit(integrationConfig, values),
            )}
            className="flex flex-col"
          >
            <ConnectionForm
              integration={integrationConfig}
              isLoading={props.isLoading}
              authorizeMethods={[
                AuthorizeMethod.Self,
                AuthorizeMethod.External,
              ]}
              onExternalAuthLinkCreated={() => setExternalAuthLinkCreated(true)}
              onAuthorizeMethodChanged={setAuthorizeMethod}
            />
          </form>
          {showWhitelistInfo(props.integration.id) && (
            <IPWhitelistInfo
              integrationId={props.integration.id}
              className="mt-8"
            />
          )}
        </CustomConnectorAccessGuard>
      </Content>
      <Footer className="flex-row-reverse">
        {externalAuthLinkCreated ? (
          <ActionButton
            colorScheme="secondary"
            variant="ghost"
            onClick={() => {
              props.onClose();
            }}
            className="mr-auto"
          >
            Close
          </ActionButton>
        ) : (
          <>
            <ConnectionFormSubmitButton
              integration={integrationConfig}
              isLoading={props.isLoading}
              isDisabled={
                (isCustomConnector(props.integration.id) &&
                  !customConnectorEnabled) ||
                authorizeMethod === AuthorizeMethod.External
              }
            />
            <NavButton
              colorScheme="secondary"
              variant="ghost"
              onClick={() => props.onCancel()}
            >
              Back
            </NavButton>
          </>
        )}
      </Footer>
    </FormProvider>
  );
}

const showWhitelistInfo = (integrationId: string) => {
  const dbConnectors = [
    //My-SQL
    "mysql",
    "adobe-commerce",
    "amazon-aurora-mysql",
    "mysql-on-amazon-rds",
    "mysql-on-google-cloud-sql",
    "mysql-on-azure-database",
    "wordpress",
    "woocommerce",
    "yoast-seo",
    "wordfence-security",
    "typo-three",
    "spree-commerce",
    "os-commerce",
    "presta-shop",
    "magento",
    "magento-on-amazon-rds",
    "plausible-analytics",
    "memberpress",
    "joomla",
    "invision-community",
    "loaded-commerce",
    "monster-insights",
    "contact-form-seven",

    //MS-SQL
    "mssql",
    "sql-server-on-rds",
    "microsoft-dynamics-365",

    //Postgres
    "postgresql",
    "supabase",
    "amazon-aurora-postgresql",
    "heroku-postgres",
    "phoenix-via-postgresql",
    "postgresql-on-azure-database",
    "postgresql-on-google-cloud",
    "postgresql-on-rds",

    //MongoDB
    "mongodb",
    "meteor-via-mongodb",
    "mongodb-sharded-cluster",
    "node-bb",
  ];

  return dbConnectors.includes(integrationId);
};
